import React, { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { GripVertical } from "lucide-react";
import { Switch } from "@/components/ui/switch"; // shadcn/ui switch

interface MealPreferencesProps {
  preferences: string[];
  mealPriorities: any;
  onUpdatePreferences: (newPreferences: string[]) => void;
  // Optional callback if parent wants to know which meals are enabled/disabled
  onToggleChange?: (meal: string, enabled: boolean) => void;
}

const MealPreferences: React.FC<MealPreferencesProps> = ({
  preferences,
  onUpdatePreferences,
  mealPriorities,
  onToggleChange,
}) => {
  // Track toggle state for each meal
  // const [enabledMap, setEnabledMap] = useState<Record<string, boolean>>(
  //   () =>
  //     preferences.reduce(
  //       (acc, meal) => ({ ...acc, [meal]: true }), // default all to true
  //       {}
  //     )
  // );
  const [enabledMap, setEnabledMap] = useState<Record<string, boolean>>(mealPriorities);

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    const items = Array.from(preferences);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    onUpdatePreferences(items);
  };

  const handleToggle = (meal: string, value: boolean) => {
    setEnabledMap((prev) => ({ ...prev, [meal]: value }));
    onToggleChange?.(meal, value);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-gray-800 dark:text-white">
        Meal Priorities
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400">
        Drag to reorder meals. Use the switch to enable or disable each meal.
      </p>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="meal-preferences">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="space-y-2"
            >
              {preferences.map((meal, index) => (
                <Draggable key={meal} draggableId={meal} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      className="flex items-center p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                    >
                      {/* Drag handle */}
                      <div {...provided.dragHandleProps} className="mr-3 text-gray-400">
                        <GripVertical size={20} />
                      </div>

                      {/* Meal name */}
                      <span className="text-gray-800 dark:text-white capitalize">
                        {meal}
                      </span>

                      {/* Priority text */}
                      <span className="ml-auto mr-4 text-sm text-gray-500 dark:text-gray-400">
                        Priority {index + 1}
                      </span>

                      {/* On/Off switch */}
                      <Switch
                        checked={enabledMap[meal]}
                        onCheckedChange={(val: any) => handleToggle(meal, val)}
                        className="relative h-6 w-11 cursor-pointer rounded-full data-[state=unchecked]:bg-red-500 transition-colors data-[state=checked]:bg-green-500"
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

export default MealPreferences;
